<script lang="ts">
  import { fade } from 'svelte/transition';
  import type { TextFormatType } from 'lexical';
  import { onDestroy } from 'svelte';

  import { Logger } from '@/Logger';
  import type { TextBlock } from '@/teoCore/TeoApi';
  import type { AnalysisResult } from '@/teoCore/text/AnalysisResult';
  import { useIsCurrentTextSet } from '@/teoCore/text/hooks/use-is-current-text-set';
  import type { TeoText } from '@/teoCore/text/TeoText';
  import CTAButton from '@/ui/elements/buttons/CTAButton.svelte';
  import TeoBallLoading from '@/ui/elements/teo-ball/TeoBallLoading.svelte';
  import { useCoreStore } from '@/ui/state/use-core-state.svelte';
  import { useActiveText, useTeoEventObserver } from '@/utils/use-teo-event-observer.svelte';

  import EditorBackgroundMarking from './cmps/EditorBackgroundMarking.svelte';
  import EditorFooter from './cmps/EditorFooter.svelte';
  import EditorForegroundMarking from './cmps/EditorForegroundMarking.svelte';
  import EditorSettings from './cmps/EditorSettings.svelte';
  import { editorTheme } from './config';
  import { EditorMarkerManager } from './EditorMarkerManager.svelte';
  import EditorContent from './lexical/EditorContent';

  const LOG = Logger('Editor');

  let { text, mode }: { text: TeoText; mode: 'generator' | 'editor' } = $props();

  let editorElement: HTMLElement;
  let containerElement: HTMLElement | undefined = $state(undefined);
  let editorContent: EditorContent | undefined = $state(undefined);
  let markers: EditorMarkerManager | undefined = $state(undefined);
  const lexicalEditorTheme = editorTheme;

  const fontSizes = ['text-xs', 'text-sm', 'text-lmd'];
  let fontSizeIndex = $state(1);
  let undoDisabled = $state(true);
  let redoDisabled = $state(true);
  let numWords = $state(0);
  let showPlaceholder = $state(text.text === '' && mode !== 'generator');
  let generatorUI = useActiveText().current.generatorUi;
  let editableText = useTeoEventObserver({
    initialValue: text.isEditable,
    update: (_eventKey, value) => value,
    target: text.events,
    events: ['editable'],
  });
  let analysing = useTeoEventObserver({
    initialValue: text.analysisPending,
    update: (_eventKey, value) => value,
    target: text.events,
    events: ['analysisPending'],
  });
  let CS = useCoreStore();
  let loading = $derived($CS.loading || analysing.current);
  let GS = $derived(text.generatorUi.store);
  let staleGeneratorHistory = $derived(!$GS.currentHistory.locked);

  let initialized = $state(false);

  let removeEventListeners: (() => void) | undefined;
  let hasText = useIsCurrentTextSet();
  let editable = $derived(editableText.current && mode !== 'generator');

  $effect(() => {
    // the editorElement seems to update often, we only need to mount the editor once
    // TODO: is there a better way to handle this?
    if (!initialized && editorElement && containerElement) {
      removeEventListeners = onHTMLElements(editorElement, containerElement);
      initialized = true;
    }
  });

  $effect(() => {
    // reference fontSizeIndex to trigger update
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    fontSizeIndex;
    updateSizeChange();
  });

  onDestroy(() => {
    removeEventListeners?.();
    markers?.destroy();
  });

  function updateSizeChange() {
    markers?.sizeChangeUpdate();
  }

  function onHTMLElements(editorElement: HTMLElement, containerElement: HTMLElement) {
    markers = new EditorMarkerManager(text, containerElement);
    editorContent = new EditorContent(editorElement, 'teo-editor', markers, lexicalEditorTheme);
    const eventListenerRemovers: (() => void)[] = [];
    eventListenerRemovers.push(
      editorContent.events.addEventListener('contentChanged', onContentChanged),
      editorContent.events.addEventListener('formattingChanged', onFormattingChanged),
      editorContent.events.addEventListener('canUndo', onCanUndoChange),
      editorContent.events.addEventListener('canRedo', onCanRedoChange),
      text.events.addEventListener('textChangedExternal', updateText),
      text.events.addEventListener('gotAnalysis', onResult),
    );

    initEditorContentText(() => {
      if (editorContent) {
        if (markers) {
          markers.editorContent = editorContent;
        }
      }
    });
    return () => {
      eventListenerRemovers.forEach((remover) => remover());
    };
  }

  function initEditorContentText(callback?: () => void) {
    // editorContent?.addContent(text.text);
    if (!text.serialized) return editorContent?.setContent(text.text, callback);
    LOG.log('IMPORTING JSON');
    editorContent?.importJSON(text.serialized, callback);
  }

  function updateText() {
    initEditorContentText();
  }

  function onResult(result: AnalysisResult) {
    numWords = result.getNumWords();
  }

  function onContentChanged(event: {
    content: string;
    getTextArray: () => TextBlock[];
    serialized: string;
  }) {
    showPlaceholder = event.content === '';
    text.updateText(event.content, event.serialized, false);
    text.updateTextArrayFunction(event.getTextArray);
  }

  function onFormattingChanged(serialized: string) {
    text.updateText(undefined, serialized, false);
  }

  function onListClick() {
    editorContent?.toggleUList();
  }

  function onTextFormatClick(format: TextFormatType) {
    editorContent?.formatText(format);
  }

  function onUndoClick() {
    editorContent?.undo();
  }

  function onRedoClick() {
    editorContent?.redo();
  }

  function onCanUndoChange(canUndo: boolean) {
    undoDisabled = !canUndo;
  }

  function onCanRedoChange(canRedo: boolean) {
    redoDisabled = !canRedo;
  }

  function onCopyClick() {
    const text = editorContent?.getText();
    navigator.clipboard.writeText(text ?? '');
  }

  function onExportClick() {
    editorContent?.exportDocx(text.ui.state.title);
  }

  async function createTextTab() {
    generatorUI.createTextTab({ view: 'performance' });
  }
</script>

<div class="flex h-full max-h-full min-h-[104px] flex-col rounded-md bg-white p-4 shadow-card">
  {#if mode === 'generator'}
    <div class="fr-cs gap-2 border-b border-b-c-divider pb-4">
      <CTAButton
        iconLeft="TachoSymbol"
        onclick={createTextTab}
        disabled={!hasText.current}
        variant="cta">Text bearbeiten</CTAButton
      >
    </div>
  {:else}
    <EditorSettings
      {onListClick}
      {onTextFormatClick}
      {onUndoClick}
      {onRedoClick}
      {undoDisabled}
      {redoDisabled}
      disabled={!editable}
    />
  {/if}
  <div class="relative flex min-h-0 flex-grow">
    <div class="w-full overflow-auto" transition:fade={{ duration: 400 }}>
      <div class="relative flex min-h-0 flex-grow" bind:this={containerElement}>
        {#if initialized && markers}
          <div class="absolute inset-0">
            {#each markers.markings.current as marking (marking.id)}
              <EditorBackgroundMarking {marking} markings={markers.markings} />
            {/each}
          </div>
        {/if}
        {#if showPlaceholder}
          <div class="absolute pl-11 pt-11 font-editor text-c-disabled {fontSizes[fontSizeIndex]}">
            <span class="text-c-disabled">Füge hier deinen Text ein.</span>
          </div>
        {/if}
        <div
          id="lexical-editor"
          class="relative min-h-0 flex-grow p-11 font-editor outline-none
      {editable ? ''
          : mode === 'generator' && !loading && !staleGeneratorHistory ? 'text-c-light/90'
          : 'text-c-disabled'} 
      {fontSizes[fontSizeIndex]}"
          contenteditable={editable}
          bind:this={editorElement}
        ></div>
        {#if initialized && markers}
          <div class="absolute left-0 top-0">
            {#each markers.markings.current as marking (marking.id)}
              {#if marking.clickable}
                <EditorForegroundMarking {marking} markings={markers.markings} />
              {/if}
            {/each}
          </div>
        {/if}
      </div>
    </div>
    {#if loading}
      <div
        class="pointer-events-none absolute inset-0 flex items-center justify-center"
        transition:fade={{ duration: 200 }}
      >
        <TeoBallLoading class="h-[130px] w-full" messages={text.editorLoadingMessages} />
      </div>
    {/if}
  </div>
  <!-- {#if editorContent && markers}
    <EditorDebug {editorContent} {markers}></EditorDebug>
  {/if} -->
  <EditorFooter bind:fontSizeIndex {fontSizes} {onCopyClick} {onExportClick} {numWords} />
</div>
